/** @jsxImportSource @emotion/react */
import React from 'react';
import css from '@emotion/css';
import styled from 'styled-components/macro';
import Paper from '../../components/layouts/Paper';
import { FetchCallbacksContext } from '../FetchCallbacks';

import Button from '../../components/Button';
import { LOAD_FORM } from '../../store/modules/state/formBuilder';
import { useDropzone } from 'react-dropzone';
import Checkbox from '@material-ui/core/Checkbox';
import CloudUpload from '@material-ui/icons/CloudUpload';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextString from '../../components/Text';
import SuccessIcon from '@material-ui/icons/CheckCircleTwoTone';

const DropTarget = styled.div`
  & {
    min-height: 120px;
    background: ${({ theme, isDragActive }) =>
      isDragActive ? theme.palette.primary.main : 'rgba(181, 190, 227, 0.05)'};
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    color: ${({ isDragActive }) => (isDragActive ? '#fff' : '#444')};
    text-shadow: ${({ isDragActive }) =>
      isDragActive ? '0 0 10px #fff' : 'none'};
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-direction: column;
    padding: 12px;
    outline: 0;
    transition: background-color 0.2s linear, color 0.2s linear,
      fill 0.2s linear, text-shadow 0s linear, box-shadow 0s linear;
    z-index: 0;
    margin: 0 auto;

    .dropzone-input {
      z-index: -20;
      left: 0;
      top: 0;
      position: absolute;
      overflow: hidden;
      clip: rect(0 0 0 0);
      height: 1px;
      width: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
    }
  }
`;

const env = process.env.REACT_APP_S3_ENV || 'production';

export default function FormBuilderTemplate({ form, formId, dispatch }) {
  const { fetchAPI, openSnackbar } = React.useContext(FetchCallbacksContext);

  const published = Boolean(form.templatesInfo && form.templatesInfo.published);

  const [submitting, setSubmitting] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [isPaid, setIsPaid] = React.useState(Boolean(form.templatesInfo && form.templatesInfo.paid));

  const changePublishStatus = React.useCallback(
    async status => {
      setSubmitting(true);

      try {
        // POST
        await fetchAPI(`/forms/v1/templates/${formId}/publish/${status}`, {
          method: 'PUT'
        });

        // GET
        const r = await fetchAPI(`/forms/v1/forms/${formId}`);
        dispatch({ type: LOAD_FORM, payload: { ...r, id: formId } });

        openSnackbar(
          'success',
          `Template ${status === 'true' ? 'published' : 'unpubished'}.`
        );
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, fetchAPI, formId, openSnackbar]
  );

  const publish = React.useCallback(() => {
    changePublishStatus('true');
  }, [changePublishStatus]);

  const unpublish = React.useCallback(() => {
    changePublishStatus('false');
  }, [changePublishStatus]);

  const icon =
    form && form.templatesInfo && form.templatesInfo.icon
      ? form.templatesInfo.icon
      : null;

  const onDrop = React.useCallback(
    async ([file]) => {
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);

      try {
        // POST
        await fetchAPI(`/forms/v1/templates/${formId}/image`, {
          method: 'PUT',
          body: formData
        });

        // GET
        const r = await fetchAPI(`/forms/v1/forms/${formId}`);

        dispatch({
          type: LOAD_FORM,
          payload: {
            ...r,
            id: formId
          }
        });

        openSnackbar('success', 'Thumbnail updated.');
      } finally {
        setUploading(false);
      }
    },
    [dispatch, fetchAPI, formId, openSnackbar]
  );

  const dropzone = useDropzone({ onDrop });

  const dropzoneInputProps = dropzone.getInputProps();

  const clickedPaidToggle = val => {
    let booleanVal = Boolean(val);
    fetchAPI(`/forms/v1/templates/${formId}/update`, {
      method: 'PUT',
      body: {
        paid: booleanVal
      } 
    }).then(() => {
      setIsPaid(booleanVal);
    });
  };

  const date =
    form &&
    form.templatesInfo &&
    form.templatesInfo.publicationDate &&
    new Date(+form.templatesInfo.publicationDate).toLocaleString();

  return (
    <Paper
      theme="light"
      align="left"
      width={500}
      // padding={0}
      style={{ margin: '20px auto 0' }}
    >
      <h2>Publish template </h2>
      {published ? (
        <>
          <p>
            <SuccessIcon
              css={css`
                color: ${({ theme }) => theme.palette.success.main};
                vertical-align: middle;
                margin-right: 0.2ch;
                position: relative;
                top: -2px;
              `}
            />{' '}
            {date ? `Publish date: ${date}` : 'Published'}
          </p>
          <p style={{ textAlign: 'center' }}>
            <Button
              data-testid="unpublish-template"
              onClick={unpublish}
              pending={submitting}
              variant="outlined"
            >
              Unpublish
            </Button>
          </p>
        </>
      ) : (
        <>
          <p>This form is not published as a template.</p>
          <p style={{ textAlign: 'center' }}>
            <Button
              data-testid="publish-template"
              onClick={publish}
              pending={submitting}
            >
              Publish
            </Button>
          </p>
        </>
      )}

      <FormControlLabel
        control={
          <Checkbox
            color="primary"
            data-testid={`formbuilder-template-paid`}
            checked={isPaid}
            onChange={e => {
              clickedPaidToggle(e.target.checked);
            }}
          />
        }
        label={
          <TextString content='form-builder.template.paid' />
        }
      />

      <h2>Template thumbnail</h2>
      <p>Upload a thumbnail image that will appear in the template preview.</p>

      <DropTarget
        {...dropzone.getRootProps()}
        isDragActive={dropzone.isDragActive}
        style={{
          width: 200,
          height: 200,
          // margin: 0,
          backgroundImage:
            icon && !uploading
              ? `url(https://vinciworks-customer-assets.s3.eu-west-2.amazonaws.com/${env}/publisher/templates/icons/${icon})`
              : null,
          backgroundSize: 'contain'
        }}
      >
        <div>
          {/* the actual input*/}
          <input
            {...dropzoneInputProps}
            data-testid="dropzone-input"
            disabled={false}
          />
          {!icon && (
            <>
              <CloudUpload style={{ fontSize: 64 }} />
              <p>Drag or click</p>
            </>
          )}
          {uploading && <CircularProgress />}
        </div>
      </DropTarget>
    </Paper>
  );
}
